import React, { useEffect, useState } from 'react';
import { Modal, InputControlled, SelectControlled, Button, Icon } from 'components';
import { useForm, useWatch, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {getInstitutionsOptions} from 'utils/functions';
import {yesOrNo_OPTIONS, sex_OPTIONS} from 'utils/constants'
import { showError } from 'utils/alerts';
import Swal from 'sweetalert2';
import { postUser, postDoctor } from 'services/users'
import * as yup from 'yup';

interface NewUserProps {
	visible: boolean;
	toggle: Function;
	fetchUsers?: Function;
}

interface IFormInputs {
	email: string;
	first_name: string;
	last_name: string;
	nickname: string;
	gender: {
		value: string,
		label: string
	};
	isAdmin: boolean;
	institutionId: {
		value: number,
		label: string
	};
	password: string,
}

export const NewUserForm = ({ visible, toggle, fetchUsers }: NewUserProps) => {
	const [loading, setLoading] = useState(false);
	const [institutionsOptions, setInstitutionsOptions] = useState([])
	const { t } = useTranslation();

	const schema = yup
		.object({
			isAdmin: yup.boolean().required(t('error.requiredField')),
			email: yup
				.string()
				.email(t('error.invalidEmail'))
				.required(t('error.requiredEmail')),
			nickname: yup.string().required(t('error.requiredField')),
			first_name: yup.string().when('isAdmin', {
				is: (isAdmin: any) => isAdmin?.value == false,
				then: yup.string().required(t('error.requiredField'))
			})
			,
			last_name: yup.string().when('isAdmin', {
				is: (isAdmin: any) => isAdmin?.value == false,
				then: yup.string().required(t('error.requiredField'))
			})
			,
			institutionId: yup.string().when('isAdmin', {
				is: (isAdmin: any) => isAdmin?.value == false,
				then: yup.string().required(t('error.requiredField'))
			})
			,
			gender: yup.string().when('isAdmin', {
				is: (isAdmin: any) => isAdmin?.value == false,
				then: yup.string().required(t('error.requiredField'))
			})
			,
			password: yup.string()
				.matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])\S{8,99}$/, t('error.invalidPassword'))
				.required(t('error.requiredField'))
		})
		.required();

	const {
		handleSubmit,
		control,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		resolver: yupResolver(schema),
	});

	const isAdmin = useWatch({
		control,
		name: "isAdmin",
	});

	const handleOnSubmit: SubmitHandler<IFormInputs> = async (data) => {
		try {
			setLoading(true);
			console.log({data})
			let formattedData = isAdmin ? 
				{
					nickname: data.nickname,
					email: data.email,
					password: data.password,
				}
				:
				{
					user: { 
						nickname: data.nickname,
						email: data.email,
						password: data.password,
					},
					institutionId: data?.institutionId,
					first_name: data?.first_name,
					last_name: data?.last_name,
					gender: data?.gender,
				}
			isAdmin ? await postUser(formattedData) : await postDoctor(formattedData);
			Swal.fire({
				title: 'User created successfully',
				icon: 'success',
			})
			reset();
			fetchUsers && fetchUsers();
			toggle();
		} catch (error) {
			showError('Error on trying to create a new user.');
		} finally {
			setLoading(false);
		}
	};

	useEffect( () => {
		fetch();
	}, [])

	const fetch = async () => {
		try {
			let options = await getInstitutionsOptions();
			setInstitutionsOptions(options);
		} catch (error) {
			console.log(error)
		}
	}

	return (
		<Modal visible={visible} toggle={toggle}>
			<div className='p-8 max-w-full' style={{width: '700px'}}>
				<div className='flex flex-row justify-between items-center mb-6'>
					<div className='text-2xl text-orange'>{isAdmin === undefined ? t('newUser') : isAdmin ? t('newAdmin') : t('newDoctor')}</div>
					<Icon name="Close" rounded className="border-orange border p-2" onClick={toggle}/>
				</div>
				<form 
					onSubmit={handleSubmit(handleOnSubmit)}
				>
					<div
						className='grid lg:grid-cols-4 grid-cols-1 gap-6'
					>
						<InputControlled
							name='nickname'
							control={control}
							className='lg:col-span-2 mb-5'
							defaultValue={''}
							labelClass='sm'
							label={t('nickname')}
							placeholder={t('nickname')}
						/>
						<SelectControlled
							name='isAdmin'
							control={control}
							className='lg:col-span-2 mb-5'
							labelClass='sm'
							options={yesOrNo_OPTIONS}
							label={t('isAdmin')}
							placeholder={t('isAdmin')}
						/>
						{isAdmin === false &&
						<>
							<InputControlled
								name='first_name'
								control={control}
								className='lg:col-span-2 mb-5'
								labelClass='sm'
								label={t('firstName')}
								placeholder={t('firstName')}
							/>
							<InputControlled
								name='last_name'
								control={control}
								className='lg:col-span-2 mb-5'
								labelClass='sm'
								label={t('lastName')}
								placeholder={t('lastName')}
							/>
							<SelectControlled
								name='institutionId'
								control={control}
								className='lg:col-span-2 mb-5'
								labelClass='sm'
								options={institutionsOptions}
								label={t('institution')}
								placeholder={t('institution')}
							/>
							<SelectControlled
								name='gender'
								control={control}
								className='lg:col-span-2 mb-5'
								labelClass='sm'
								options={sex_OPTIONS}
								label={t('gender')}
								placeholder={t('gender')}
							/>
						</>
						}
						<InputControlled
							name='email'
							control={control}
							defaultValue={''}
							className='lg:col-span-2 mb-5'
							labelClass='sm'
							label={t('email')}
							placeholder={t('userEmail')}
						/>
						<InputControlled
							name='password'
							control={control}
							defaultValue={''}
							className='lg:col-span-2'
							labelClass='sm'
							label={t('password')}
							placeholder={t('password')}
						/>
					</div>
					
					<Button
						type='submit'
						loading={loading}
						className='c-orange font-extrabold text-sm mt-6 lg:col-span-4'
					>
						+ {t('createUser')}
					</Button>
					{/* <div className="flex justify-between mt-8">
                    <Button type="link" to="/auth/forgot-password" className="c-orange text-sm">{t('forgotPassword')}</Button>
                </div> */}
				</form>
			</div>
		</Modal>
	);
};
